export class DetectionOverlay {
    constructor(elementId = 'detectionOverlay') {
        this.element = document.getElementById(elementId);
        if (!this.element) {
            throw new Error(`DetectionOverlay: Element with id '${elementId}' not found.`);
        }
        
        this.isVisible = false;
        this.detectionStarted = false; // Pour savoir si la détection a démarré
        this.lastDetectionTime = 0;
        this.detectionTimeout = 100; // Délai en ms avant de montrer l'overlay si pas de détection
        this.hideTimeout = null;
        this.showTimeout = null;
        this.currentType = 'face'; // 'face' ou 'pose'
        
        // Initialiser le style de l'overlay
        this.initializeOverlayStyle();
        
        // Initialiser les gestionnaires d'événements
        this.initEventListeners();
    }

    // Initialiser le style de l'overlay
    initializeOverlayStyle() {
        // Appliquer les styles de base à l'overlay
        this.element.style.position = 'absolute';
        this.element.style.top = '50%';
        this.element.style.left = '50%';
        this.element.style.transform = 'translate(-50%, -50%)';
        this.element.style.zIndex = '9000';
        this.element.style.display = 'none';
        this.element.style.flexDirection = 'column';
        this.element.style.alignItems = 'center';
        this.element.style.justifyContent = 'center';
        this.element.style.color = '#fff';
        this.element.style.fontFamily = 'Arial, sans-serif';
        this.element.style.fontSize = '0.9rem';
        this.element.style.textAlign = 'center';
        this.element.style.transition = 'opacity 0.3s ease-in-out';
        this.element.style.opacity = '0';
        this.element.style.backgroundRepeat = 'no-repeat';
        this.element.style.backgroundPosition = 'center top';
        this.element.style.backgroundSize = 'contain';
        this.element.style.padding = '0';
        
        // S'assurer que le span est positionné en bas
        const span = this.element.querySelector('span');
        if (span) {
            span.style.marginTop = 'auto';
            span.style.padding = '20px';
            span.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            span.style.borderRadius = '8px';
            span.style.margin = '10px';
        }
        
        // Définir l'image d'overlay initial
        this.updateOverlayImage();
    }

    // Mettre à jour l'image d'overlay en fonction du type
    updateOverlayImage() {
        const imagePath = this.currentType === 'face' ? 
            'gui/overlay_face.png' : 
            'gui/overlay_pose.png';
        this.element.style.backgroundImage = `url('${imagePath}')`;
        
        // Ajuster la taille en fonction de l'écran
        this.adjustOverlaySize();
    }

    // Ajuster la taille de l'overlay en fonction de l'écran
    adjustOverlaySize() {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        if (screenWidth > screenHeight) {
            // Mode paysage : 50% de la largeur
            this.element.style.width = `${screenWidth * 0.5}px`;
            this.element.style.height = `${screenWidth * 0.5}px`;
        } else {
            // Mode portrait : 50% de la hauteur
            this.element.style.width = `${screenHeight * 0.5}px`;
            this.element.style.height = `${screenHeight * 0.5}px`;
        }
    }

    // Définir le type d'overlay (face ou pose)
    setOverlayType(type) {
        if (type === 'face' || type === 'pose') {
            this.currentType = type;
            this.updateOverlayImage();
        }
    }

    // Appelé quand la détection démarre (première fois)
    onDetectionStarted() {
        this.detectionStarted = true;
    }

    // Appelé quand une détection est active
    onDetectionActive() {
        this.lastDetectionTime = Date.now();
        
        // Annuler le timeout de show si il y en a un
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
        
        // Cacher l'overlay immédiatement si visible
        if (this.isVisible) {
            this.hide();
        }
    }

    // Appelé quand aucune détection n'est active
    onDetectionLost() {
        // Ne pas montrer l'overlay si la détection n'a jamais démarré
        if (!this.detectionStarted) {
            return;
        }
        
        // Annuler le timeout de hide si il y en a un
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        
        // Programmer l'affichage de l'overlay après un délai
        if (!this.showTimeout && !this.isVisible) {
            this.showTimeout = setTimeout(() => {
                this.show();
                this.showTimeout = null;
            }, this.detectionTimeout);
        }
    }

    // Afficher l'overlay
    show() {
        if (!this.isVisible) {
            this.adjustOverlaySize(); // Ajuster la taille avant d'afficher
            this.element.style.display = 'flex';
            this.element.style.opacity = '1';
            this.isVisible = true;
        }
    }

    // Cacher l'overlay
    hide() {
        if (this.isVisible) {
            this.element.style.opacity = '0';
            this.hideTimeout = setTimeout(() => {
                this.element.style.display = 'none';
                this.isVisible = false;
                this.hideTimeout = null;
            }, 300); // Attendre la fin de la transition CSS
        }
    }

    // Forcer l'affichage (par exemple au démarrage)
    forceShow() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
        this.adjustOverlaySize(); // Ajuster la taille avant d'afficher
        this.element.style.display = 'flex';
        this.element.style.opacity = '1';
        this.isVisible = true;
    }

    // Forcer le masquage
    forceHide() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
        this.element.style.display = 'none';
        this.element.style.opacity = '0';
        this.isVisible = false;
    }

    // Mettre à jour le message
    setMessage(message) {
        const span = this.element.querySelector('span');
        if (span) {
            span.textContent = message;
        }
    }

    // Initialiser les gestionnaires d'événements
    initEventListeners() {
        // Redimensionner l'overlay quand la fenêtre change de taille
        window.addEventListener('resize', () => {
            if (this.isVisible) {
                this.adjustOverlaySize();
            }
        });
    }
}
